import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import type { Order, OrderStatus } from '../types';

interface MatrixMapProps {
  orders: Order[];
}

export function MatrixMap({ orders }: MatrixMapProps) {
  const completedOrders = orders.filter(o => !o.abandoned);
  const abandonedOrders = orders.filter(o => o.abandoned);

  // Status breakdown
  const statusCounts: Record<OrderStatus, number> = {
    unknown: 0,
    automation: 0,
    intervention: 0,
    escalation: 0,
  };
  for (const order of completedOrders) {
    statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
  }

  const statusData = [
    { name: 'Unknown', value: statusCounts.unknown, status: 'unknown' as OrderStatus, color: '#8ea9cf' },
    { name: 'Automation', value: statusCounts.automation, status: 'automation' as OrderStatus, color: '#10b981' },
    { name: 'Intervention', value: statusCounts.intervention, status: 'intervention' as OrderStatus, color: '#f59e0b' },
    { name: 'Escalation', value: statusCounts.escalation, status: 'escalation' as OrderStatus, color: '#ef4444' },
  ].filter(s => s.value > 0);

  // Time to close distribution
  const timeBuckets = [
    { label: '< 1 min', min: 0, max: 60000, count: 0 },
    { label: '1-5 min', min: 60000, max: 300000, count: 0 },
    { label: '5-15 min', min: 300000, max: 900000, count: 0 },
    { label: '15-30 min', min: 900000, max: 1800000, count: 0 },
    { label: '30-60 min', min: 1800000, max: 3600000, count: 0 },
    { label: '> 1 hr', min: 3600000, max: Infinity, count: 0 },
  ];

  for (const order of completedOrders) {
    if (order.checkoutSuccess && order.sessionStart) {
      const duration = new Date(order.checkoutSuccess).getTime() - new Date(order.sessionStart).getTime();
      const bucket = timeBuckets.find(b => duration >= b.min && duration < b.max);
      if (bucket) bucket.count++;
    }
  }

  // Revenue by status
  const revenueByStatus = [
    { name: 'Unknown', value: completedOrders.filter(o => o.status === 'unknown').reduce((s, o) => s + o.total, 0), color: '#8ea9cf' },
    { name: 'Automation', value: completedOrders.filter(o => o.status === 'automation').reduce((s, o) => s + o.total, 0), color: '#10b981' },
    { name: 'Intervention', value: completedOrders.filter(o => o.status === 'intervention').reduce((s, o) => s + o.total, 0), color: '#f59e0b' },
    { name: 'Escalation', value: completedOrders.filter(o => o.status === 'escalation').reduce((s, o) => s + o.total, 0), color: '#ef4444' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Status Breakdown */}
      <div className="oem-card p-5">
        <h3 className="font-display text-lg text-oem-navy mb-4">Order Status Breakdown</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={4}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'white',
                  border: '1px solid #dce6f5',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-4 mt-2">
          {statusData.map(s => (
            <div key={s.name} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="text-xs text-oem-600">{s.name}: {s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Time to Close Distribution */}
      <div className="oem-card p-5">
        <h3 className="font-display text-lg text-oem-navy mb-4">Time to Close Distribution</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timeBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dce6f5" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'white',
                  border: '1px solid #dce6f5',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              <Bar dataKey="count" fill="#1a4a8a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue by Status */}
      <div className="oem-card p-5">
        <h3 className="font-display text-lg text-oem-navy mb-4">Revenue by Close Type</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueByStatus} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#dce6f5" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip
                formatter={(value) => [`$${Number(value ?? 0).toLocaleString()}`, 'Revenue']}
                contentStyle={{
                  background: 'white',
                  border: '1px solid #dce6f5',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {revenueByStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 bg-oem-100 rounded-lg">
            <p className="text-xs text-oem-muted">Completed Orders</p>
            <p className="text-lg font-bold text-oem-navy">{completedOrders.length}</p>
          </div>
          <div className="p-3 bg-oem-100 rounded-lg">
            <p className="text-xs text-oem-muted">Abandoned Carts</p>
            <p className="text-lg font-bold text-status-abandoned">{abandonedOrders.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
